import type { GeoPoint, Mission } from './types.js';
import { parseGameDate, parseGameTime } from './time.js';
import { isShortMapsUrl, parseMapsUrl } from './geo.js';
import { extractYouTubeId } from './youtube.js';

/**
 * שמות העמודות בגיליון "משימות" — אין לשנות אותם.
 * העמודה האחרונה נקראת "יוטיוב" בגיליון בפועל; "YouTube" מתקבל כחלופה.
 * "תמונה" (עמודה K) ו"פתרון נדרש" (עמודה L) הן הרחבות אופציונליות — גיליון בלעדיהן עדיין תקין.
 */
export const COLUMN_HEADERS = {
  number: '#',
  hour: 'שעה',
  day: 'יום',
  date: 'תאריך',
  location: 'אפשור מיקום',
  time: 'אפשור זמן',
  manual: 'אפשור ידני',
  task: 'המשימה',
  preparations: 'הכנות',
  youtube: 'יוטיוב',
  image: 'תמונה',
  solution: 'פתרון נדרש',
} as const;

export type ColumnKey = keyof typeof COLUMN_HEADERS;

const YOUTUBE_ALIASES = ['יוטיוב', 'youtube', 'yt'];

export type HeaderIndex = Record<ColumnKey, number>;

const norm = (c: unknown): string => String(c ?? '').trim().toLowerCase();

/**
 * ממפה שורת כותרות לאינדקסי עמודות. מחזיר null אם עמודת חובה חסרה.
 * עמיד לסדר עמודות שונה ולעמודות ריקות נוספות בסוף. "תמונה" ו"פתרון נדרש" אופציונליות —
 * גיליון בלעדיהן עדיין ממופה בהצלחה (אינדקס -1, מטופל כשדה ריק בכל מקום אחר).
 */
export function mapHeader(cells: unknown[]): HeaderIndex | null {
  const idx: Partial<HeaderIndex> = {};
  const findExact = (name: string) => cells.findIndex((c) => norm(c) === norm(name));
  for (const key of Object.keys(COLUMN_HEADERS) as ColumnKey[]) {
    if (key === 'youtube') {
      idx.youtube = cells.findIndex((c) => YOUTUBE_ALIASES.includes(norm(c)));
    } else {
      idx[key] = findExact(COLUMN_HEADERS[key]);
    }
  }
  const required: ColumnKey[] = [
    'number',
    'hour',
    'day',
    'date',
    'location',
    'time',
    'manual',
    'task',
    'preparations',
  ];
  for (const key of required) {
    if ((idx[key] ?? -1) < 0) return null;
  }
  return idx as HeaderIndex;
}

/** שדות משימה כפי שהם מופיעים בשורת גיליון */
export interface SheetRowFields {
  missionNumber: number | null;
  hour: string;
  day: string;
  date: string;
  locationUrl: string;
  unlockTimeRaw: string;
  manualRaw: string;
  task: string;
  preparations: string;
  youtubeUrl: string;
  imageUrl: string;
  requiredSolution: string;
}

const cellAt = (cells: unknown[], i: number): string =>
  i >= 0 && i < cells.length ? String(cells[i] ?? '').trim() : '';

/** ממיר שורת גיליון לשדות משימה */
export function rowToFields(cells: unknown[], h: HeaderIndex): SheetRowFields {
  const numRaw = cellAt(cells, h.number);
  const num = numRaw === '' ? null : Number.parseInt(numRaw, 10);
  return {
    missionNumber: num !== null && Number.isFinite(num) ? num : null,
    hour: cellAt(cells, h.hour),
    day: cellAt(cells, h.day),
    date: cellAt(cells, h.date),
    locationUrl: cellAt(cells, h.location),
    unlockTimeRaw: cellAt(cells, h.time),
    manualRaw: cellAt(cells, h.manual),
    task: cellAt(cells, h.task),
    preparations: cellAt(cells, h.preparations),
    youtubeUrl: cellAt(cells, h.youtube),
    imageUrl: cellAt(cells, h.image),
    requiredSolution: cellAt(cells, h.solution),
  };
}

/** ערכים הנחשבים כסימון חיובי בעמודת "אפשור ידני" (בגיליון בפועל: V) */
const MANUAL_TRUTHY = new Set(['v', 'x', 'true', '✓', '✔', 'כן', '1', 'yes']);

export function isManualEnabled(raw: string | null | undefined): boolean {
  return MANUAL_TRUTHY.has(norm(raw));
}

/** הסבר ברור מה לא תקין בתאריך של משימה עם "אפשור זמן" — במיוחד תאריך בלי שנה ("16/04") שמופיע הרבה בגיליונות */
function dateProblem(raw: string): string {
  const d = raw.trim();
  if (!d) return '"תאריך" חסר — אפשור זמן דורש תאריך מלא (למשל 19/04/2026)';
  if (/^\d{1,2}[./-]\d{1,2}$/.test(d)) {
    return `"תאריך" ללא שנה ("${d}") — כתבו גם שנה, למשל ${d.replace(/[.-]/g, '/')}/2026`;
  }
  return `"תאריך" לא תקין ("${d}") — הפורמט הנדרש הוא 19/04/2026`;
}

/**
 * בדיקת תקינות שדות משימה. מחזירה רשימת בעיות בעברית (ריקה = תקין).
 * שורה פגומה אינה מפילה את המערכת — היא מסומנת ומוצגת למנהל.
 */
export function validateFields(
  f: SheetRowFields,
  resolved?: GeoPoint | null,
): string[] {
  const reasons: string[] = [];
  if (f.missionNumber === null) reasons.push('חסר מספר משימה בעמודת #');
  if (!f.task.trim()) reasons.push('עמודת "המשימה" ריקה');
  if (f.unlockTimeRaw.trim()) {
    if (!parseGameTime(f.unlockTimeRaw)) reasons.push('שעת "אפשור זמן" אינה תקינה');
    if (!parseGameDate(f.date)) reasons.push(dateProblem(f.date));
  }
  if (f.locationUrl.trim() && !resolved && !parseMapsUrl(f.locationUrl)) {
    reasons.push(
      isShortMapsUrl(f.locationUrl)
        ? 'קישור מיקום מקוצר — ממתין לפענוח בשרת (אם זה נשאר יותר מדקה: הדביקו קישור מלא או קואורדינטות כמו 32.0853,34.7818)'
        : 'קישור המיקום אינו מזוהה',
    );
  }
  if (f.youtubeUrl.trim() && !extractYouTubeId(f.youtubeUrl)) {
    reasons.push('כתובת היוטיוב אינה מזוהה');
  }
  return reasons;
}

/**
 * בונה שורת תאים לכתיבה חזרה לגיליון, לפי מפת העמודות בפועל —
 * כך שסדר עמודות שונה בגיליון לא ישבור את הסנכרון.
 */
export function fieldsToRow(f: SheetRowFields, h: HeaderIndex): string[] {
  const width = Math.max(...Object.values(h)) + 1;
  const row = new Array<string>(width).fill('');
  const put = (i: number, v: string) => {
    if (i >= 0) row[i] = v;
  };
  put(h.number, f.missionNumber === null ? '' : String(f.missionNumber));
  put(h.hour, f.hour);
  put(h.day, f.day);
  put(h.date, f.date);
  put(h.location, f.locationUrl);
  put(h.time, f.unlockTimeRaw);
  put(h.manual, f.manualRaw);
  put(h.task, f.task);
  put(h.preparations, f.preparations);
  put(h.youtube, f.youtubeUrl);
  put(h.image, f.imageUrl);
  put(h.solution, f.requiredSolution);
  return row;
}

/** מערך תאים קנוני בסדר העמודות המוגדר — לטביעת אצבע יציבה גם אם סדר העמודות בגיליון שונה */
export function canonicalCells(f: SheetRowFields): string[] {
  return [
    f.missionNumber === null ? '' : String(f.missionNumber),
    f.hour,
    f.day,
    f.date,
    f.locationUrl,
    f.unlockTimeRaw,
    f.manualRaw,
    f.task,
    f.preparations,
    f.youtubeUrl,
    f.imageUrl,
    // בסוף בכוונה: תא ריק בסוף נחתך בחישוב ההאש, כך שטביעות האצבע של שורות בלי פתרון נדרש לא השתנו
    f.requiredSolution,
  ];
}

/** טביעת אצבע של תוכן משימה — לזיהוי שינויים בין סנכרונים */
export function fieldsFingerprint(f: SheetRowFields): string {
  return hashRow(canonicalCells(f));
}

/** חילוץ שדות הגיליון מתוך אובייקט משימה מלא */
export function missionToFields(m: Mission): SheetRowFields {
  return {
    missionNumber: m.missionNumber,
    hour: m.hour,
    day: m.day,
    date: m.date,
    locationUrl: m.locationUrl,
    unlockTimeRaw: m.unlockTimeRaw,
    manualRaw: m.manualRaw,
    task: m.task,
    preparations: m.preparations,
    youtubeUrl: m.youtubeUrl,
    imageUrl: m.imageUrl,
    requiredSolution: m.requiredSolution,
  };
}

/** האש יציב של תוכן שורה — לזיהוי שינויים בגיליון בין סנכרונים */
export function hashRow(cells: unknown[]): string {
  // FNV-1a 32bit — מהיר, ללא תלות ב-crypto, זהה בדפדפן ובשרת.
  // מפריד U+001F בין תאים + חיתוך תאים ריקים בסוף (עמודות ריקות בגיליון).
  const SEP = '';
  const text = cells
    .map((c) => String(c ?? '').trim())
    .join(SEP)
    .replace(new RegExp(SEP + '+$'), '');
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}
